/**
 * demoWeeklyReports — mock data for weekly report management
 *
 * Mirrors noticesApi method names used by WeeklyReport tabs:
 *   getCurrentWeek, getMyReports, getTeamReports, getOrgSummary,
 *   getNonSubmitters, listAggregates, getAggregate, getAggregateSources,
 *   generateAggregate, updateAggregate, regenerateAggregate, submitAggregate
 */

const NOW = () => Date.now() / 1000;
const DAY = 86400;

const weekRange = () => {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - today.getDay() + 1);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  return {
    week_start: monday.toISOString().slice(0, 10),
    week_end: friday.toISOString().slice(0, 10),
  };
};

// Team member reports (full content) — reused by getTeamReports and getAggregateSources
const teamMemberReports = [
  {
    id: 101,
    employee_id: 'TS2600008',
    employee_name: '김지현',
    position: '선임연구원',
    org_unit_id: 3,
    org_unit_name: 'AI Lab',
    status: 'submitted',
    submitted_at: NOW() - DAY,
    content: {
      this_week:
        '데이터 파이프라인 리팩토링 완료. ETL 성능 40% 향상 달성. ' +
        '신규 임베딩 모델 벤치마크 진행. [긴급] 프로덕션 DB migration 이슈 발생 - 복구 필요.',
      next_week: '모니터링 대시보드 구축, QA 테스트 지원, migration 후속 점검',
      issues: '인프라팀 지원 필요 (DB 복구 작업)',
      notes: '주말 온콜 예정',
    },
  },
  {
    id: 102,
    employee_id: 'TS2600012',
    employee_name: '이영희',
    position: '책임연구원',
    org_unit_id: 3,
    org_unit_name: 'AI Lab',
    status: 'submitted',
    submitted_at: NOW() - DAY * 1.2,
    content: {
      this_week:
        'LLM 파인튜닝 실험 3건 진행 (Llama3-8B, Qwen2-7B, Gemma-7B). ' +
        '사내 QA 데이터셋 2천건 구축 완료. 논문 초안 섹션 2/5 작성.',
      next_week: 'LLM 벤치마크 논문 초안 마무리, 실험 결과 내부 공유',
      issues: '없음',
      notes: '',
    },
  },
  {
    id: 103,
    employee_id: 'TS2600018',
    employee_name: '박민수',
    position: '연구원',
    org_unit_id: 3,
    org_unit_name: 'AI Lab',
    status: 'draft',
    submitted_at: null,
    content: {
      this_week:
        '추천 시스템 A/B 테스트 설정 진행 중 (50% 완료). ' +
        '[블로커] GPU 리소스 부족으로 대규모 실험 대기 중. H100 2장 추가 할당 필요.',
      next_week: 'A/B 테스트 런칭, 초기 결과 분석',
      issues: 'GPU 리소스 블로커 해소 필수',
      notes: '',
    },
  },
  {
    id: 104,
    employee_id: 'TS2600021',
    employee_name: '정수진',
    position: '연구원',
    org_unit_id: 3,
    org_unit_name: 'AI Lab',
    status: 'not_started',
    submitted_at: null,
    content: { this_week: '', next_week: '', issues: '', notes: '' },
  },
  {
    id: 105,
    employee_id: 'TS2600027',
    employee_name: '최재원',
    position: '선임연구원',
    org_unit_id: 3,
    org_unit_name: 'AI Lab',
    status: 'submitted',
    submitted_at: NOW() - DAY * 0.5,
    content: {
      this_week:
        '추론 서버 레이턴시 최적화 완료 (p99 320ms → 180ms). ' +
        'vLLM 업그레이드 및 배치 스케줄러 튜닝. 신규 모니터링 지표 12개 추가.',
      next_week: '추론 서버 오토스케일링 정책 재설계, 온콜 핸드북 업데이트',
      issues: '없음',
      notes: '',
    },
  },
];

export const demoWeeklyReportsService = {
  // ========== Individual (나의 주간보고) ==========
  getCurrentWeek: async () => {
    const { week_start, week_end } = weekRange();
    return {
      week_start,
      week_end,
      status: 'draft',
      content: { body: '', this_week: '', next_week: '', issues: '', notes: '' },
      // MYB-389: structured escalation fields default to disabled
      escalation_enabled: 0,
      escalation_type: null,
      escalation_text: null,
      escalation_visibility: 'manager_only',
    };
  },

  // Write operations — demo no-ops (prevents crashes when user clicks save/submit in demo mode)
  createReport: async (_data: { content: any; org_unit_id?: number }) => ({
    id: 9999,
    week_start: weekRange().week_start,
    status: 'draft',
    created_at: NOW(),
  }),
  updateReport: async (id: number, _data: { content: any }) => ({ id, updated_at: NOW() }),
  submitReport: async (id: number) => ({ id, status: 'submitted', submitted_at: NOW() }),

  getMyReports: async (_limit?: number) => ({
    items: [
      {
        id: 1,
        week_start: '2026-03-30',
        week_end: '2026-04-03',
        submitted_at: NOW() - DAY * 3,
        status: 'reviewed',
        reviewer_comment: '좋습니다. 다음 주 일정도 명확해서 공유하기 좋았습니다.',
        content: {
          body: `<h3>이번 주 업무</h3>
<ul>
  <li>데이터 파이프라인 리팩토링 완료 - ETL 성능 40% 향상</li>
  <li>주간 코드 리뷰 6건 진행</li>
  <li>LLM 파인튜닝 실험 초기 설정</li>
</ul>
<h3>다음 주 계획</h3>
<ul>
  <li>모니터링 대시보드 구축</li>
  <li>QA 테스트 지원</li>
</ul>`,
          this_week: '리포트 대시보드 리뉴얼 1차 QA 완료. 사용자 피드백 12건 반영.',
          next_week: '2차 QA 및 오픈 준비, 운영 가이드 문서화',
          issues: '없음',
          notes: '',
        },
        // MYB-389: example structured escalation (bubbles to division level only)
        escalation_enabled: 1,
        escalation_type: 'urgent',
        escalation_text: '프로덕션 DB migration 이슈로 인해 인프라팀 지원이 필요합니다. 복구 작업 중 데이터 정합성 검증이 지연되고 있어 일정 조정이 필요합니다.',
        escalation_visibility: 'division',
      },
      {
        id: 2,
        week_start: '2026-03-23',
        week_end: '2026-03-27',
        submitted_at: NOW() - DAY * 10,
        status: 'submitted',
        content: {
          this_week: '권한 관리 모듈 리팩토링, 단위테스트 커버리지 72% → 88%',
          next_week: '리포트 대시보드 리뉴얼 착수',
          issues: '없음',
          notes: '',
        },
      },
      {
        id: 3,
        week_start: '2026-03-16',
        week_end: '2026-03-20',
        submitted_at: NOW() - DAY * 17,
        status: 'reviewed',
        reviewer_comment: '커버리지 향상 수고 많으셨습니다.',
        content: {
          this_week: 'RBAC Phase 3 QA 지원, 스테이징 배포 후 안정화',
          next_week: '권한 관리 모듈 리팩토링 진행',
          issues: '없음',
          notes: '',
        },
      },
      {
        id: 4,
        week_start: '2026-03-09',
        week_end: '2026-03-13',
        submitted_at: NOW() - DAY * 24,
        status: 'submitted',
        content: {
          this_week: 'DB 암호화 마이그레이션 작업 공동 참여, 백업 검증',
          next_week: 'RBAC Phase 3 QA 지원',
          issues: '없음',
          notes: '',
        },
      },
    ],
    count: 4,
  }),

  // ========== Team (팀원 현황) ==========
  getTeamReports: async (weekStart?: string) => {
    const wk = weekStart || weekRange().week_start;
    // Deterministic seed from weekStart
    const variantSeed = parseInt(wk.slice(-2), 10) || 0;

    // Build 3 teams under the same division for a 'division'-level view demo.
    // Each team has realistic Korean members with 제출/작성중/미작성 mix.
    const mkStatus = (n: number, s: number, d: number): Array<'submitted' | 'draft' | 'not_started'> => {
      const arr: Array<'submitted' | 'draft' | 'not_started'> = [];
      for (let i = 0; i < s; i++) arr.push('submitted');
      for (let i = 0; i < d; i++) arr.push('draft');
      while (arr.length < n) arr.push('not_started');
      return arr;
    };

    const aiLabMembers = [
      { employee_id: 'TS2600008', employee_name: '김지현', position: '선임연구원',
        content: {
          this_week: '데이터 파이프라인 리팩토링 완료. ETL 성능 40% 향상 달성. [긴급] 프로덕션 DB migration 이슈 발생 - 복구 필요.',
          next_week: '모니터링 대시보드 구축, QA 테스트 지원, migration 후속 점검',
          issues: '인프라팀 지원 필요 (DB 복구 작업)',
          notes: '주말 온콜 예정' } },
      { employee_id: 'TS2600012', employee_name: '이영희', position: '책임연구원',
        content: {
          this_week: 'LLM 파인튜닝 실험 3건 진행 (Llama3-8B, Qwen2-7B, Gemma-7B). 사내 QA 데이터셋 2천건 구축 완료.',
          next_week: 'LLM 벤치마크 논문 초안 마무리, 실험 결과 내부 공유',
          issues: '없음', notes: '' } },
      { employee_id: 'TS2600018', employee_name: '박민수', position: '연구원',
        content: {
          this_week: '추천 시스템 A/B 테스트 설정 진행 중. [블로커] GPU 리소스 부족으로 실험 대기 중. H100 2장 추가 할당 필요.',
          next_week: 'A/B 테스트 런칭, 초기 결과 분석',
          issues: 'GPU 리소스 블로커 해소 필수', notes: '' } },
      { employee_id: 'TS2600027', employee_name: '최재원', position: '선임연구원',
        content: {
          this_week: '추론 서버 레이턴시 최적화 완료 (p99 320ms → 180ms). vLLM 업그레이드 및 배치 스케줄러 튜닝.',
          next_week: '추론 서버 오토스케일링 정책 재설계, 온콜 핸드북 업데이트',
          issues: '없음', notes: '' } },
      { employee_id: 'TS2600033', employee_name: '장유진', position: '연구원',
        content: { this_week: 'RAG 시스템 평가 지표 구축, 정확도 벤치마크 8종 수행.', next_week: '평가 자동화 파이프라인 구성', issues: '없음', notes: '' } },
      { employee_id: 'TS2600041', employee_name: '윤태민', position: '연구원',
        content: { this_week: '멀티모달 모델 POC, 이미지-텍스트 임베딩 통합 실험.', next_week: 'POC 결과 보고서 작성', issues: '없음', notes: '' } },
      { employee_id: 'TS2600049', employee_name: '임소영', position: '연구원',
        content: { this_week: '학습 데이터 품질 검증 자동화 스크립트 개발.', next_week: '데이터 레이블링 가이드 업데이트', issues: '없음', notes: '' } },
      { employee_id: 'TS2600055', employee_name: '조현우', position: '연구원',
        content: { this_week: '프롬프트 엔지니어링 실험, 5개 태스크 대상 성능 측정.', next_week: '실험 결과 팀 공유', issues: '없음', notes: '' } },
    ];

    const platformMembers = [
      { employee_id: 'TS2600061', employee_name: '한지훈', position: '책임연구원',
        content: { this_week: '플랫폼 API Gateway 리팩토링 완료. P99 레이턴시 230ms → 140ms.', next_week: '캐시 전략 재설계', issues: '없음', notes: '' } },
      { employee_id: 'TS2600064', employee_name: '강민지', position: '선임연구원',
        content: { this_week: '권한 관리 모듈 단위테스트 커버리지 72% → 88%. [긴급] 토큰 만료 버그 수정.', next_week: '리프레시 토큰 흐름 개선', issues: 'QA 지원 필요', notes: '' } },
      { employee_id: 'TS2600068', employee_name: '이준호', position: '선임연구원',
        content: { this_week: '이벤트 버스 마이그레이션 (Redis → Kafka) 3/5 완료.', next_week: '잔여 컨슈머 마이그레이션', issues: '없음', notes: '' } },
      { employee_id: 'TS2600072', employee_name: '서지원', position: '연구원',
        content: { this_week: '모니터링 지표 14개 추가, Grafana 대시보드 2종 신규.', next_week: 'SLO 정의 문서화', issues: '없음', notes: '' } },
      { employee_id: 'TS2600076', employee_name: '박도현', position: '연구원',
        content: { this_week: 'CI/CD 파이프라인 개선, 빌드 시간 8분 → 4분 단축.', next_week: '배포 자동화 스크립트 리팩토링', issues: '없음', notes: '' } },
      { employee_id: 'TS2600079', employee_name: '김서연', position: '연구원',
        content: { this_week: 'DB 스키마 마이그레이션 툴 검증.', next_week: '운영 DB 적용 리허설', issues: '없음', notes: '' } },
      { employee_id: 'TS2600083', employee_name: '정우성', position: '연구원',
        content: { this_week: 'API 문서 자동화 (OpenAPI 3.1) 적용.', next_week: '포털 개편', issues: '없음', notes: '' } },
      { employee_id: 'TS2600088', employee_name: '홍지민', position: '연구원',
        content: { this_week: '로그 수집 파이프라인 개선, 인덱스 사이즈 30% 감소.', next_week: '알람 룰 튜닝', issues: '없음', notes: '' } },
      { employee_id: 'TS2600092', employee_name: '오세훈', position: '연구원',
        content: { this_week: 'gRPC 서비스 프로토콜 정의 표준화.', next_week: '레거시 REST 엔드포인트 정리', issues: '없음', notes: '' } },
      { employee_id: 'TS2600096', employee_name: '유나영', position: '연구원',
        content: { this_week: '단위테스트 리팩토링, mock 구조 정리.', next_week: '테스트 픽스처 통합', issues: '없음', notes: '' } },
    ];

    const dataMembers = [
      { employee_id: 'TS2600101', employee_name: '신재민', position: '책임연구원',
        content: { this_week: '데이터 레이크 파티셔닝 재설계, 쿼리 비용 25% 감소.', next_week: '실시간 스트리밍 파이프라인 설계', issues: '없음', notes: '' } },
      { employee_id: 'TS2600105', employee_name: '류현진', position: '선임연구원',
        content: { this_week: 'BI 대시보드 12종 리뉴얼. [블로커] 데이터 품질 이슈 — 정합성 재검증 필요.', next_week: '데이터 품질 모니터링 자동화', issues: '데이터 엔지니어 지원 필요', notes: '' } },
      { employee_id: 'TS2600109', employee_name: '김다은', position: '연구원',
        content: { this_week: 'dbt 모델 23개 리팩토링, 테스트 커버리지 95%.', next_week: '메타데이터 관리 도구 도입 검토', issues: '없음', notes: '' } },
      { employee_id: 'TS2600114', employee_name: '안재현', position: '연구원',
        content: { this_week: '분석 쿼리 최적화, 대시보드 로딩 3.2s → 1.1s.', next_week: '임베디드 분석 기능 개발', issues: '없음', notes: '' } },
      { employee_id: 'TS2600118', employee_name: '손예진', position: '연구원',
        content: { this_week: '데이터 카탈로그 온보딩 5팀 완료.', next_week: '나머지 팀 온보딩', issues: '없음', notes: '' } },
      { employee_id: 'TS2600122', employee_name: '차은우', position: '연구원',
        content: { this_week: 'Airflow DAG 리팩토링, 작업 실패율 3.2% → 0.8%.', next_week: '재시도 정책 표준화', issues: '없음', notes: '' } },
    ];

    // Status distribution varies by week seed
    const aiV = [{ s: 6, d: 1, n: 1 }, { s: 7, d: 1, n: 0 }, { s: 5, d: 2, n: 1 }][variantSeed % 3];
    const plV = [{ s: 8, d: 1, n: 1 }, { s: 9, d: 1, n: 0 }, { s: 7, d: 2, n: 1 }][variantSeed % 3];
    const dtV = [{ s: 4, d: 1, n: 1 }, { s: 5, d: 1, n: 0 }, { s: 3, d: 2, n: 1 }][variantSeed % 3];

    const applyStatus = (
      members: any[],
      statuses: Array<'submitted' | 'draft' | 'not_started'>,
    ) =>
      members.map((m, idx) => ({
        ...m,
        status: statuses[idx] ?? 'submitted',
        submitted_at: statuses[idx] === 'submitted' ? NOW() - DAY * (0.5 + (idx % 3) * 0.3) : null,
        content: statuses[idx] === 'not_started' ? undefined : m.content,
      }));

    const aiLabFinal = applyStatus(aiLabMembers, mkStatus(aiLabMembers.length, aiV.s, aiV.d));
    const platformFinal = applyStatus(platformMembers, mkStatus(platformMembers.length, plV.s, plV.d));
    const dataFinal = applyStatus(dataMembers, mkStatus(dataMembers.length, dtV.s, dtV.d));

    const mkGroup = (id: number, name: string, parent: string | null, members: any[]) => {
      const submitted = members.filter((m) => m.status === 'submitted').length;
      const draft = members.filter((m) => m.status === 'draft').length;
      const not_started = members.filter((m) => m.status === 'not_started').length;
      return {
        org_unit_id: id,
        org_unit_name: name,
        parent_name: parent,
        summary: { total: members.length, submitted, draft, not_started },
        members,
      };
    };

    const groups = [
      mkGroup(3, 'AI Lab', '개발혁신본부', aiLabFinal),
      mkGroup(4, '플랫폼개발팀', '개발혁신본부', platformFinal),
      mkGroup(5, '데이터팀', '개발혁신본부', dataFinal),
    ];

    const overall = groups.reduce(
      (acc, g) => ({
        total: acc.total + g.summary.total,
        submitted: acc.submitted + g.summary.submitted,
        draft: acc.draft + g.summary.draft,
        not_started: acc.not_started + g.summary.not_started,
      }),
      { total: 0, submitted: 0, draft: 0, not_started: 0 },
    );

    // Backward-compat flat items
    const items: any[] = [];
    for (const g of groups) {
      for (const m of g.members) {
        items.push({ ...m, org_unit_id: g.org_unit_id, org_unit_name: g.org_unit_name, week_start: wk });
      }
    }

    return {
      week_start: wk,
      viewer_level: 'division' as const,
      viewer_org_unit_id: 1,
      summary: overall,
      groups,
      items,
      count: items.length,
    };
  },

  // ========== Org Summary (전사 현황) ==========
  getOrgSummary: async (
    weekStart?: string,
    _includeDepts: boolean = true,
    _includeTrend: boolean = true
  ) => {
    const wk = weekStart || weekRange().week_start;
    // Deterministic seed from weekStart — keeps WoW/MoM comparisons consistent
    const seed = (() => {
      let h = 0;
      for (let i = 0; i < wk.length; i++) h = (h * 31 + wk.charCodeAt(i)) & 0xfffffff;
      return h;
    })();
    const pick = (mod: number, offset: number = 0) => ((seed + offset) % mod + mod) % mod;

    // Base: total 37, vary submitted/draft/not_started gradually
    const totalEmployees = 37;
    const submittedVariation = pick(8); // 0..7
    const submitted = 25 + submittedVariation; // 25..32
    const draft = 3 + pick(4, 2); // 3..6
    const notStarted = Math.max(0, totalEmployees - submitted - draft);
    const rate = Math.round((submitted / totalEmployees) * 1000) / 10;

    // Department variation — keep totals constant, vary rates
    const deptBase = [
      { org_unit_id: 1, org_unit_name: '개발본부', parent_name: null, total: 15 },
      { org_unit_id: 2, org_unit_name: '사업개발본부', parent_name: null, total: 8 },
      { org_unit_id: 3, org_unit_name: 'AI Lab', parent_name: '개발본부', total: 6 },
      { org_unit_id: 4, org_unit_name: '경영지원', parent_name: null, total: 5 },
      { org_unit_id: 5, org_unit_name: '연구소', parent_name: null, total: 3 },
    ];
    const departments = deptBase.map((d, idx) => {
      const rShift = pick(3, idx * 7) - 1; // -1..1
      const baseSubmitted = Math.max(0, Math.min(d.total, d.total - 1 - rShift));
      const dDraft = Math.max(0, Math.min(d.total - baseSubmitted, 1 + (pick(2, idx * 5))));
      const dNotStarted = Math.max(0, d.total - baseSubmitted - dDraft);
      const dRate = d.total > 0 ? Math.round((baseSubmitted / d.total) * 1000) / 10 : 0;
      return {
        ...d,
        submitted: baseSubmitted,
        draft: dDraft,
        not_started: dNotStarted,
        rate: dRate,
      };
    });

    return {
      week_start: wk,
      total_employees: totalEmployees,
      submitted,
      draft,
      not_started: notStarted,
      submission_rate: rate,
      departments,
      trend: [
        { week_start: '2026-03-09', total: 37, submitted: 29, rate: 78.4 },
        { week_start: '2026-03-16', total: 37, submitted: 31, rate: 83.8 },
        { week_start: '2026-03-23', total: 37, submitted: 33, rate: 89.2 },
        { week_start: '2026-03-30', total: 37, submitted: 28, rate: 75.7 },
        { week_start: wk, total: totalEmployees, submitted, rate },
      ],
    };
  },

  getNonSubmitters: async (_weekStart?: string, _orgUnitId?: number) => ({
    items: [
      {
        employee_id: 'TS2600015',
        name: '오재훈',
        org_unit_name: '경영지원',
        email: 'ohjh@tscorp.ai',
      },
      {
        employee_id: 'TS2600022',
        name: '한지수',
        org_unit_name: '사업개발본부',
        email: 'han.js@tscorp.ai',
      },
      {
        employee_id: 'TS2600031',
        name: '서하늘',
        org_unit_name: '경영지원',
        email: 'seo.hn@tscorp.ai',
      },
      {
        employee_id: 'TS2600021',
        name: '정수진',
        org_unit_name: 'AI Lab',
        email: 'jung.sj@tscorp.ai',
      },
    ],
    count: 4,
  }),

  // ========== Aggregate Reports (집계 보고서) ==========
  listAggregates: async (params?: { level?: string; week_start?: string; status?: string }) => {
    // 전체 집계 목록 — org_hierarchy(demoEmployees.getTeams)의 org_unit_id와 매치
    const allItems = [
      // Teams
      {
        id: 301,
        level: 'team',
        org_unit_id: 3,
        org_unit_name: 'AI Lab',
        status: 'draft',
        generation_lock: 0,
        pending_regen: 0,
        generated_at: NOW() - 3600,
      },
      {
        id: 303,
        level: 'team',
        org_unit_id: 5,
        org_unit_name: '플랫폼팀',
        status: 'submitted',
        generation_lock: 1,
        pending_regen: 0,
        generated_at: NOW() - 4200,
      },
      {
        id: 304,
        level: 'team',
        org_unit_id: 7,
        org_unit_name: '영업팀',
        status: 'draft',
        generation_lock: 0,
        pending_regen: 1,
        generated_at: NOW() - DAY,
      },
      // Divisions
      {
        id: 302,
        level: 'division',
        org_unit_id: 1,
        org_unit_name: '개발본부',
        status: 'submitted',
        generation_lock: 1,
        pending_regen: 0,
        generated_at: NOW() - 7200,
      },
      // Executive
      {
        id: 310,
        level: 'executive',
        org_unit_id: 100,
        org_unit_name: 'TSCORP',
        status: 'draft',
        generation_lock: 0,
        pending_regen: 0,
        generated_at: NOW() - 10800,
      },
    ].map(i => ({
      ...i,
      week_start: weekRange().week_start,
      ai_model: 'gpt-4o',
    }));
    const items = params?.level
      ? allItems.filter(i => i.level === params.level)
      : allItems;
    return { items, count: items.length };
  },

  getAggregate: async (id: number) => {
    const isTeam = id !== 302;
    const summary_html = `<h3>Executive Summary</h3>
<p>금주 AI Lab은 데이터 파이프라인 리팩토링을 완료하여 ETL 성능이 40% 향상되었습니다. LLM 벤치마크 실험 3건과 추론 서버 레이턴시 최적화(p99 320ms → 180ms)가 주요 성과입니다. 다만 프로덕션 DB migration 이슈와 GPU 리소스 부족이 블로커로 확인되어 즉시 대응이 필요합니다.</p>
<h3>주요 성과</h3>
<ul>
  <li>ETL 파이프라인 리팩토링 완료, 성능 40% 향상 (김지현)</li>
  <li>LLM 파인튜닝 실험 3건 진행, QA 데이터셋 2천건 구축 (이영희)</li>
  <li>추론 서버 p99 레이턴시 320ms → 180ms 개선 (최재원)</li>
  <li>추천 시스템 A/B 테스트 설정 50% 진행 (박민수)</li>
</ul>
<h3>주요 이슈</h3>
<ul>
  <li><strong>프로덕션 DB migration 이슈</strong> — 서비스 영향 가능성, 데이터 정합성 리스크 <em>(담당: 김지현)</em></li>
  <li><strong>GPU 리소스 부족 (H100 2장 필요)</strong> — 대규모 실험 대기, Q2 목표 달성 지연 우려 <em>(담당: 박민수)</em></li>
</ul>
<h3>다음 주 계획</h3>
<ul>
  <li>모니터링 대시보드 구축 및 QA 테스트 지원</li>
  <li>LLM 벤치마크 논문 초안 마무리</li>
  <li>추천 시스템 A/B 테스트 런칭 및 초기 결과 분석</li>
  <li>추론 서버 오토스케일링 정책 재설계</li>
</ul>`;
    const content = {
      summary_html,
      executive_summary:
        '금주 AI Lab은 데이터 파이프라인 리팩토링을 완료하여 ETL 성능이 40% 향상되었습니다. ' +
        'LLM 벤치마크 실험 3건과 추론 서버 레이턴시 최적화 (p99 320ms → 180ms)가 주요 성과입니다. ' +
        '다만 프로덕션 DB migration 이슈와 GPU 리소스 부족이 블로커로 확인되어 즉시 대응이 필요합니다.',
      key_achievements: [
        'ETL 파이프라인 리팩토링 완료, 성능 40% 향상 (김지현)',
        'LLM 파인튜닝 실험 3건 진행, QA 데이터셋 2천건 구축 (이영희)',
        '추론 서버 p99 레이턴시 320ms → 180ms 개선 (최재원)',
        '추천 시스템 A/B 테스트 설정 50% 진행 (박민수)',
      ],
      key_issues: [
        {
          issue: '프로덕션 DB migration 이슈',
          impact: '서비스 영향 가능성, 데이터 정합성 리스크',
          owner: '김지현',
        },
        {
          issue: 'GPU 리소스 부족 (H100 2장 필요)',
          impact: '대규모 실험 대기, Q2 목표 달성 지연 우려',
          owner: '박민수',
        },
      ],
      next_week_plan: [
        '모니터링 대시보드 구축 및 QA 테스트 지원',
        'LLM 벤치마크 논문 초안 마무리',
        '추천 시스템 A/B 테스트 런칭 및 초기 결과 분석',
        '추론 서버 오토스케일링 정책 재설계',
      ],
      escalations: [
        {
          type: 'urgent',
          text: '[긴급] 프로덕션 DB migration 이슈 발생 - 인프라팀 지원 필요',
          source: '김지현',
        },
        {
          type: 'blocker',
          text: '[블로커] GPU 리소스 부족으로 실험 대기 중 - H100 2장 추가 할당 요청',
          source: '박민수',
        },
      ],
    };
    return {
      id,
      level: isTeam ? 'team' : 'division',
      org_unit_id: isTeam ? 3 : 1,
      org_unit_name: isTeam ? 'AI Lab' : '개발본부',
      week_start: weekRange().week_start,
      week_end: weekRange().week_end,
      status: isTeam ? 'draft' : 'submitted',
      generation_lock: isTeam ? 0 : 1,
      pending_regen: 0,
      generated_at: NOW() - 3600,
      ai_model: 'gpt-4o',
      final_content_json: JSON.stringify(content),
      ai_draft_json: JSON.stringify(content),
    };
  },

  getAggregateSources: async (_id: number) => ({
    items: teamMemberReports.map((r) => ({
      id: r.id,
      employee_id: r.employee_id,
      employee_name: r.employee_name,
      position: r.position,
      org_unit_id: r.org_unit_id,
      org_unit_name: r.org_unit_name,
      status: r.status,
      submitted_at: r.submitted_at,
      content: r.content,
    })),
    count: teamMemberReports.length,
  }),

  generateAggregate: async (data: {
    level?: string;
    org_unit_id?: number;
    week_start?: string;
  }) => ({
    status: 'success',
    aggregate_id: 301,
    level: data.level || 'team',
    org_unit_id: data.org_unit_id || 3,
    week_start: data.week_start || weekRange().week_start,
    generated_at: NOW(),
    ai_model: 'gpt-4o',
  }),

  updateAggregate: async (id: number, _final_content_json: any) => ({
    success: true,
    id,
    generation_lock: 1,
    updated_at: NOW(),
  }),

  regenerateAggregate: async (id: number, _force: boolean = false) => ({
    status: 'success',
    aggregate_id: id,
    regenerated_at: NOW(),
  }),

  submitAggregate: async (id: number) => ({
    success: true,
    id,
    status: 'submitted',
    submitted_at: NOW(),
  }),
};
